import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import colours from 'theme/atoms/colours';

import * as RechartsStyled from 'dashboard/styles/Recharts.style';

const BORDER_PADDING = 10;
const X_AXIS_HEIGHT = 30;

interface RiskLabel {
  risk_key: string;
  risk: string;
}

interface EmptyStateProps {
  labels: RiskLabel[];
}

export default function EmptyState({ labels }: EmptyStateProps) {
  const { t } = useTranslation('pageDashboard');

  const placeholderData = labels.map((item, i) => ({
    risk: item.risk,
    note_count: Math.max(4, 18 - i * 2),
  }));

  const chartHeight = Math.max(200, labels.length * 40);

  const chartStyle = {
    fontFamily: 'Inter, sans-serif',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  } as const;

  return (
    <RechartsStyled.HorizontalBarLayout>
      <RechartsStyled.HorizontalBarLabels
        $paddingTop={BORDER_PADDING}
        $paddingBottom={BORDER_PADDING}
      >
        {labels.map((item) => (
          <RechartsStyled.HorizontalBarLabel key={item.risk_key}>
            {item.risk.replace(/ Risk$/i, '')}
          </RechartsStyled.HorizontalBarLabel>
        ))}
      </RechartsStyled.HorizontalBarLabels>

      <div>
        <RechartsStyled.ChartEmptyWrapper>
          <RechartsStyled.ChartBlurredBackground>
            <RechartsStyled.ChartBorder>
              <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart
                  data={placeholderData}
                  layout="vertical"
                  margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                  style={chartStyle}
                >
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="risk" hide />
                  <Bar dataKey="note_count" barSize={10} radius={20}>
                    {placeholderData.map((_, index) => (
                      <Cell key={index} fill={colours.neutral[100]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </RechartsStyled.ChartBorder>
          </RechartsStyled.ChartBlurredBackground>

          <RechartsStyled.ChartEmptyOverlay>
            <RechartsStyled.ChartEmptyCard>
              <RechartsStyled.ChartEmptyTextContent>
                <RechartsStyled.ChartEmptyTitle>
                  {t('risk.noData')}
                </RechartsStyled.ChartEmptyTitle>
                <RechartsStyled.ChartEmptyDescription>
                  {t('risk.noDataDescription')}
                </RechartsStyled.ChartEmptyDescription>
              </RechartsStyled.ChartEmptyTextContent>
            </RechartsStyled.ChartEmptyCard>
          </RechartsStyled.ChartEmptyOverlay>
        </RechartsStyled.ChartEmptyWrapper>

        <div style={{ padding: `0 ${BORDER_PADDING + 1}px` }}>
          <ResponsiveContainer width="100%" height={X_AXIS_HEIGHT}>
            <BarChart
              data={placeholderData}
              layout="vertical"
              style={chartStyle}
            >
              <YAxis type="category" dataKey="risk" hide />
              <XAxis
                type="number"
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                tick={{
                  fill: colours.neutral[300],
                  fontSize: 13,
                  fontWeight: 650,
                }}
              />
              <Bar dataKey="note_count" fill="transparent" barSize={0} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </RechartsStyled.HorizontalBarLayout>
  );
}
